// usePlayerHealth — React hook that wires Player B telemetry into the
// ChannelsView player lifecycle.
//
// Responsibilities:
//   1. Establish a per-tab correlation/idempotency id (viewId). The frontend
//      persists it in sessionStorage and appends it as ?_vid=<id> to every
//      streaming URL. The backend keeps that id in its own viewId (which
//      also encodes uniqid + publicPath) and returns it via the
//      X-Player-View-Id response header so other endpoints (the
//      /player-health receiver, the websocket login, ...) can use it.
//      For browser-side streams we can't read response headers of a media
//      fetch, so the FE id and the BE id are correlated by publicPath +
//      timestamp window in the admin timeline.
//   2. Subscribe to mpegts.js events (MEDIA_INFO, ERROR, LOADING_COMPLETE,
//      RECOVERED, STALLED, LOAD_TIMEOUT, DESTROYING) and forward them to
//      playerHealth.
//   3. Subscribe to <video> element events (waiting, stalled, playing,
//      pause, error, ended, ratechange, ...) and forward them too.
//   4. Stop reporting on unmount.
//
// Usage:
//   const ref = usePlayerHealth(playURL, channelName);
//   ...
//   const player = mpegts.createPlayer({ url: playURL, ... });
//   player.attachMediaElement(videoElement);
//   ref.bindPlayer(player);
//   ref.bindVideoElement(videoElement);

import {useEffect, useRef, useCallback} from 'react';
import mpegts from 'mpegts.js';
import {playerHealth} from 'utils/playerHealth';
import {getOrCreateTabPlayerViewId} from 'utils/playerViewId';

function safeAttrs(args) {
  try {
    return JSON.parse(
      JSON.stringify(args, (_k, v) => {
        if (v instanceof Error) {
          return {name: v.name, message: v.message};
        }
        if (typeof ErrorEvent !== 'undefined' && v instanceof ErrorEvent) {
          return {type: 'errorEvent', message: v.message};
        }
        return v;
      })
    );
  } catch (_) {
    return {argsCount: Array.isArray(args) ? args.length : 0};
  }
}

export function usePlayerHealth(playURL, channelName) {
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const ctxRef = useRef({viewId: null, channelName: null});
  const viewIdRef = useRef(null);

  if (!viewIdRef.current) {
    viewIdRef.current = getOrCreateTabPlayerViewId();
  }

  // ── Set / clear context whenever the active channel changes ──────────
  useEffect(() => {
    if (!playURL) {
      ctxRef.current = {viewId: null, channelName: null};
      playerHealth.clearContext();
      return undefined;
    }

    // Never copy provider-bearing playback URLs into telemetry. Every channel
    // in this tab deliberately shares one id so Player C can replace the old
    // relay session immediately instead of waiting for its idle timeout.
    const viewId = viewIdRef.current;
    ctxRef.current = {viewId, channelName: channelName || null};
    playerHealth.setContext({viewId, publicPath: null, channelName: channelName || null});
    playerHealth.report('player.selected', {});

    return () => {
      playerHealth.report('player.unselected', {});
      // Best-effort flush; fire-and-forget because the effect cleanup
      // cannot await a promise.
      playerHealth.flush();
    };
  }, [playURL, channelName]);

  const bindPlayer = useCallback((player) => {
    if (!player || playerRef.current === player) return;
    playerRef.current = player;

    const wire = (eventName, semanticName) => {
      try {
        player.on(eventName, (...args) => {
          playerHealth.report(`mpegts.${semanticName}`, safeAttrs(args));
        });
      } catch (_) {
        // Older mpegts.js builds don't expose every event; skip silently.
      }
    };

    wire(mpegts.Events.MEDIA_INFO, 'mediaInfo');
    wire(mpegts.Events.ERROR, 'error');
    wire(mpegts.Events.LOADING_COMPLETE, 'loadingComplete');
    wire(mpegts.Events.RECOVERED, 'recovered');
    wire(mpegts.Events.STALLED, 'stalled');
    wire(mpegts.Events.LOAD_TIMEOUT, 'loadTimeout');
    wire(mpegts.Events.DESTROYING, 'destroying');
  }, []);

  const bindVideoElement = useCallback((el) => {
    if (!el || videoRef.current === el) return;
    videoRef.current = el;

    const handler = (name) => () => {
      try {
        playerHealth.report(`video.${name}`, {
          currentTime: el.currentTime,
          duration: isFinite(el.duration) ? el.duration : null,
          readyState: el.readyState,
          networkState: el.networkState,
          paused: el.paused,
          ended: el.ended,
          errorCode: el.error ? el.error.code : null,
          playbackRate: el.playbackRate,
          videoWidth: el.videoWidth,
          videoHeight: el.videoHeight
        });
      } catch (_) {
        // never throw from an event handler
      }
    };

    el.addEventListener('waiting', handler('waiting'));
    el.addEventListener('stalled', handler('stalled'));
    el.addEventListener('playing', handler('playing'));
    el.addEventListener('pause', handler('pause'));
    el.addEventListener('error', handler('error'));
    el.addEventListener('ended', handler('ended'));
    el.addEventListener('ratechange', handler('ratechange'));
    el.addEventListener('seeking', handler('seeking'));
    el.addEventListener('seeked', handler('seeked'));
    el.addEventListener('volumechange', handler('volumechange'));
  }, []);

  // ── Final flush on unmount ────────────────────────────────────────────
  useEffect(() => {
    return () => {
      try {
        playerHealth.flush();
      } catch (_) {
        /* noop */
      }
    };
  }, []);

  return {bindPlayer, bindVideoElement, viewId: viewIdRef.current};
}
